import React, { useEffect, useRef, useState } from "react";
import { AppFrame, TopBar } from "@/components/Shell";
import { api } from "@/lib/api";
import { useShareBaseUrl } from "@/lib/share";
import { Download, Copy } from "lucide-react";
import { toast } from "sonner";

export default function ShopQR() {
  const [settings, setSettings] = useState(null);
  const posterRef = useRef(null);
  const shareBase = useShareBaseUrl();
  const publicUrl = shareBase || (typeof window !== "undefined" ? window.location.origin : "");
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(publicUrl)}&size=600x600&margin=8&format=png`;

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings");
      setSettings(data);
    })();
  }, []);

  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Link copied");
  };

  const printPoster = () => {
    window.print();
  };

  return (
    <AppFrame>
      <TopBar title="Shop QR" subtitle="Print & display" />
      <div className="px-5 mt-4 space-y-4">
        <p className="text-sm text-neutral-600 leading-relaxed">
          Print this poster and stick it at your shop counter. Customers scan → open your live catalogue → browse everything and message you on WhatsApp.
        </p>

        {/* Printable poster */}
        <div
          ref={posterRef}
          id="shop-qr-poster"
          data-testid="qr-poster"
          className="relative rounded-3xl overflow-hidden bg-white border border-black/10 p-6 print:border-0 print:rounded-none"
        >
          <div className="text-center">
            <img
              src="/logo-full.png"
              alt="Butterfly The Fashion Store"
              className="h-20 w-auto mx-auto"
            />
            <div className="text-[10px] uppercase tracking-[0.32em] text-neutral-500 mt-3">Scan · Browse · Enquire</div>
            {settings?.store_tagline && (
              <div className="text-sm text-neutral-500 mt-2 italic">{settings.store_tagline}</div>
            )}
          </div>
          <div className="mt-6 aspect-square bg-white rounded-2xl border-4 border-[#FADADD] p-4 grid place-items-center">
            <img
              src={qrSrc}
              alt="Shop QR"
              data-testid="qr-image"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="mt-5 text-center">
            <div className="text-[11px] tracking-widest uppercase text-neutral-500">Point your camera</div>
            <div className="font-display text-lg mt-0.5">Digital Catalogue</div>
            <div className="text-[11px] text-neutral-500 mt-2 break-all">{publicUrl}</div>
          </div>
          <div className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-neutral-400">
            <span className="w-6 h-px bg-neutral-300" /> Powered by Butterfly <span className="w-6 h-px bg-neutral-300" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 print:hidden">
          <button
            data-testid="qr-copy-btn"
            onClick={copyLink}
            className="h-12 rounded-full border border-black/10 text-sm font-medium press flex items-center justify-center gap-2"
          >
            <Copy size={14} /> Copy link
          </button>
          <button
            data-testid="qr-print-btn"
            onClick={printPoster}
            className="h-12 rounded-full bg-neutral-900 text-white text-sm font-medium press flex items-center justify-center gap-2"
          >
            <Download size={14} /> Print poster
          </button>
        </div>

        <a
          data-testid="qr-download-png"
          href={qrSrc}
          download="butterfly-qr.png"
          target="_blank" rel="noopener noreferrer"
          className="block text-center text-xs underline underline-offset-4 text-neutral-600"
        >
          Download plain QR image
        </a>

        <div className="h-8" />
      </div>
    </AppFrame>
  );
}
